/* Asset-Browser (Modal): Texturen, WADs, Sprites, Skyboxen und Sounds ansehen bzw. anhören und vom Server laden.
 * Standardmäßig nur fehlende Assets (von der Karte gebraucht, im Editor nicht geladen); „Alle“ zeigt alles. */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { filterEntries, isMissing, quickFilter, textureItems } from '../../model/assets.ts';
import type { AssetEntry } from '../../model/assets.ts';
import { Dialog, DialogBody, DialogFoot } from '../dialog.tsx';
import { useT } from '../hooks.ts';
import { TexCanvas } from '../texture/widgets.tsx';
import { BROWSER_TABS } from './host.ts';
import type { AssetHost, BrowserTab, WadInfo } from './host.ts';
import { PixelsView, usePixels } from './PixelsView.tsx';

const MAX_SHOWN = 200;
const entryId = (e: AssetEntry): string => e.kind + ':' + e.key;
const kb = (n: number): string => (n >= 1 << 20 ? (n / (1 << 20)).toFixed(1) + ' MB' : Math.max(1, Math.round(n / 1024)) + ' KB');

/** Zustandszeile eines Eintrags: geladen / fehlt / nur auf dem Server / nicht auf dem Server */
export function StatusBadge({ e }: { e: AssetEntry }) {
  const t = useT();
  const [cls, key] = e.loaded ? ['ok', 'ab.loaded'] : isMissing(e) ? (e.onServer ? ['bad', 'ab.missing'] : ['bad', 'ab.missing_noserver']) : ['', 'ab.on_server'];
  return <span className={'ab-badge ' + cls}>{t(key)}</span>;
}

function EntryPreview({ host, e, big }: { host: AssetHost; e: AssetEntry; big?: boolean }) {
  const px = usePixels(entryId(e) + (e.loaded ? ':l' : ''), () => (e.kind === 'sprite' || e.kind === 'skybox') ? host.preview(e) : Promise.resolve(null));
  if (e.kind === 'sound') return <span className="ab-ico" aria-hidden="true">♪</span>;
  if (e.kind === 'wad') return <span className="ab-ico" aria-hidden="true">▤</span>;
  if (e.kind === 'model') return <span className="ab-ico" aria-hidden="true">◈</span>;
  return <PixelsView px={px} className={big ? 'big' : undefined} label={e.label} />;
}

function WadSample({ host, e }: { host: AssetHost; e: AssetEntry }) {
  const t = useT();
  const [info, setInfo] = useState<WadInfo | null | undefined>(undefined);
  useEffect(() => { let alive = true; setInfo(undefined); host.wadInfo(e).then((i) => { if (alive) setInfo(i); }, () => { if (alive) setInfo(null); }); return () => { alive = false; }; }, [host, e.key, e.loaded]);   // eslint-disable-line react-hooks/exhaustive-deps
  if (info === undefined) return <p className="small">{t('ab.loading')}</p>;
  if (!info) return <p className="small">{t('ab.no_preview')}</p>;
  return (
    <>
      <p className="small">{t('ab.wad_count', { count: info.count })}</p>
      <div className="ab-sample">{info.sample.map((s) => <div key={s.name} title={s.name}><PixelsView px={s.px} label={s.name} /><span>{s.name}</span></div>)}</div>
    </>
  );
}

function Detail({ host, e, busy, playing, onLoad, onPlay }: { host: AssetHost; e: AssetEntry; busy: boolean; playing: boolean; onLoad(): void; onPlay(): void }) {
  const t = useT();
  const sky = e.kind === 'skybox';
  const canLoad = e.onServer && (sky || !e.loaded);
  return (
    <aside className="ab-detail" aria-label={t('ab.details')}>
      <h3>{e.label}</h3>
      {(e.kind === 'sprite' || e.kind === 'skybox') && <EntryPreview host={host} e={e} big />}
      {e.kind === 'wad' && <WadSample host={host} e={e} />}
      {e.kind === 'sound' && <button className="btn" onClick={onPlay} disabled={!e.loaded && !e.onServer}>{playing ? '■ ' + t('ab.stop') : '▶ ' + t('ab.play')}</button>}
      <p className="small"><StatusBadge e={e} />{e.needed ? ' · ' + t('ab.needed') : ''}{e.size ? ' · ' + kb(e.size) : ''}</p>
      {e.paths.length > 0 && <p className="small ab-path">{e.paths.join(', ')}</p>}
      {canLoad && <button className="btn primary" disabled={busy} onClick={onLoad}>{busy ? t('ab.loading') : t(sky ? 'ab.preview_3d' : 'ab.load')}</button>}
    </aside>
  );
}

export function AssetBrowser(props: { host: AssetHost; initialTab?: BrowserTab; onClose: () => void }) {
  const t = useT();
  const { host } = props;
  const [tab, setTab] = useState<BrowserTab>(props.initialTab || 'texture');
  const [query, setQuery] = useState('');
  const [onlyUsed, setOnlyUsed] = useState(true);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [sel, setSel] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [playing, setPlaying] = useState<string | null>(null);
  const [, bump] = useState(0);
  const stop = useRef<(() => void) | null>(null);

  useEffect(() => host.subscribe(() => bump((n) => n + 1)), [host]);
  useEffect(() => { void host.refresh(); }, [host]);
  useEffect(() => () => { stop.current?.(); }, []);
  const choose = (x: BrowserTab) => { setTab(x); setSel(null); setChecked(new Set()); stop.current?.(); setPlaying(null); };

  const entries = host.entries();
  const kindOf = tab === 'texture' || tab === 'other' ? undefined : tab;
  const list = useMemo(() => (kindOf ? filterEntries(entries, { query, onlyUsed, kind: kindOf }) : []), [entries, query, onlyUsed, kindOf]);
  const tex = host.textures();
  const texList = tab === 'texture' ? textureItems(tex.loaded, tex.used, { query, onlyUsed }) : [];
  const counts = (k: BrowserTab): number => k === 'other' ? host.unclassified().length : k === 'texture' ? (onlyUsed ? tex.used.length : tex.loaded.length) : entries.filter((e) => e.kind === k && (!onlyUsed || e.needed)).length;
  const others = tab === 'other' ? quickFilter(host.unclassified(), query, (a) => a.path) : [];
  const picked = list.find((e) => entryId(e) === sel) || null;

  const load = useCallback(async (e: AssetEntry) => { setBusy(entryId(e)); try { await host.load(e, { preview: e.kind === 'skybox' }); } finally { setBusy(null); } }, [host]);
  const play = async (e: AssetEntry) => {
    const id = entryId(e);
    stop.current?.(); stop.current = null;
    if (playing === id) { setPlaying(null); return; }
    setPlaying(id);
    try { stop.current = await host.play(e, () => setPlaying((p) => (p === id ? null : p))); } catch { setPlaying(null); }
  };
  const shown = list.slice(0, MAX_SHOWN), texShown = texList.slice(0, MAX_SHOWN);
  const total = tab === 'texture' ? texList.length : tab === 'other' ? others.length : list.length;
  const noServer = !host.server() && tab !== 'texture';
  // Mehrfachauswahl: nur Dateien, die auf dem Server liegen, lassen sich auswählen und löschen
  const rows = tab === 'other' ? others.map((a) => ({ id: a.path, paths: [a.path] })) : list.filter((e) => e.onServer).map((e) => ({ id: entryId(e), paths: e.paths }));
  const rowIds = rows.map((r) => r.id);
  const chosen = rows.filter((r) => checked.has(r.id));
  const allChosen = rows.length > 0 && chosen.length === rows.length;
  const toggle = (id: string) => setChecked((c) => { const n = new Set(c); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  const toggleAll = () => setChecked(allChosen ? new Set() : new Set(rowIds));
  const removeChosen = async () => {
    const paths = chosen.flatMap((r) => r.paths);
    if (!paths.length || !window.confirm(t('ab.confirm_delete_many', { count: paths.length }))) return;
    await host.removeMany(paths);
    setChecked(new Set()); setSel(null);
  };
  const tick = (id: string, name: string) => (
    <input type="checkbox" className="ab-tick" aria-label={t('ab.select_item', { name })} checked={checked.has(id)} onClick={(ev) => ev.stopPropagation()} onChange={() => toggle(id)} />
  );

  return (
    <Dialog title={t('ab.title')} onClose={props.onClose}>
      <div className="vlv-tabs" role="tablist">
        {BROWSER_TABS.map((k) => <button key={k} role="tab" aria-selected={tab === k} onClick={() => choose(k)}>{t('ab.tab_' + k)} <span className="small">({counts(k)})</span></button>)}
      </div>
      <DialogBody>
        <div className="abr">
          <div className="row ab-bar">
            <input type="search" className="inp" style={{ flex: 1 }} aria-label={t('ab.search')} placeholder={t('ab.search')} value={query} onChange={(e) => setQuery(e.target.value)} />
            {tab !== 'other' && <label className="chk"><input type="checkbox" checked={onlyUsed} onChange={(e) => setOnlyUsed(e.target.checked)} />{t('ab.only_used')}</label>}
            <button className="btn sm" onClick={() => void host.refresh()} title={t('ab.reload_tip')}>↻</button>
          </div>
          {noServer && <p className="small">{t('ab.no_server')}</p>}
          <p className="small" role="status">{t('ab.count', { count: total }) + (total > MAX_SHOWN ? ' · ' + t('ab.first_shown', { count: MAX_SHOWN }) : '')}</p>
          {tab === 'other' && <p className="small">{t('ab.other_hint')}</p>}
          {!total && <p className="small">{onlyUsed && tab !== 'other' ? t('ab.none_used') : t('ab.none')}</p>}
          {tab !== 'texture' && rows.length > 0 && (
            <div className="row ab-bulk">
              <button className="btn sm" onClick={toggleAll}>{allChosen ? t('ab.select_none') : t('ab.select_all')}</button>
              <button className="btn sm" disabled={!chosen.length} onClick={() => void removeChosen()}>{t('ab.delete_selected', { count: chosen.length })}</button>
            </div>
          )}
          <div className="ab-main">
            {tab === 'other' ? (
              <div className="ab-list" role="list">
                {others.slice(0, MAX_SHOWN).map((a) => (
                  <div key={a.path} role="listitem" className="ab-card ab-other" data-key={a.path}>
                    {tick(a.path, a.path)}
                    <span className="ab-name" title={a.path}>{a.path}</span>
                    <span className="small">{kb(a.size)}</span>
                  </div>
                ))}
              </div>
            ) : tab === 'texture' ? (
              <div className="thumbs ab-grid" role="list">
                {texShown.map((x) => x.ok ? (
                  <button key={x.name} role="listitem" className={'thumb' + (x.name.toUpperCase() === tex.active.toUpperCase() ? ' act' : '')} data-name={x.name} title={x.name}
                    onClick={() => host.setActiveTex(x.name, false)} onDoubleClick={() => host.setActiveTex(x.name, true)}>
                    <TexCanvas name={x.name} wads={{ pixels: host.texPixels }} version={tex.version} later /><span>{x.name}</span>
                  </button>
                ) : <span key={x.name} role="listitem" className="chip miss" title={t('pn.tex_not_loaded')}>{x.name}</span>)}
              </div>
            ) : (
              <div className={tab === 'sprite' || tab === 'skybox' ? 'ab-grid cards' : 'ab-list'} role="list">
                {shown.map((e) => {
                  const id = entryId(e);
                  return (
                    <div key={id} role="listitem" tabIndex={0} className={'ab-card' + (sel === id ? ' act' : '') + (checked.has(id) ? ' chk-on' : '')} data-key={e.key} onClick={() => setSel(id)} onKeyDown={(ev) => { if (ev.key === 'Enter') setSel(id); }}>
                      {e.onServer && tick(id, e.label)}
                      <EntryPreview host={host} e={e} />
                      <span className="ab-name" title={e.label}>{e.label}</span>
                      <StatusBadge e={e} />
                      {tab === 'sound' && <button className="btn sm" aria-label={t('ab.play') + ' ' + e.label} onClick={(ev) => { ev.stopPropagation(); setSel(id); void play(e); }} disabled={!e.loaded && !e.onServer}>{playing === id ? '■' : '▶'}</button>}
                      {e.onServer && (e.kind === 'skybox' || !e.loaded) && <button className="btn sm" disabled={busy === id} onClick={(ev) => { ev.stopPropagation(); void load(e); }}>{t(e.kind === 'skybox' ? 'ab.preview_3d' : 'ab.load')}</button>}
                    </div>
                  );
                })}
              </div>
            )}
            {picked && <Detail host={host} e={picked} busy={busy === entryId(picked)} playing={playing === entryId(picked)} onLoad={() => void load(picked)} onPlay={() => void play(picked)} />}
          </div>
        </div>
      </DialogBody>
      <DialogFoot><button className="btn primary" onClick={props.onClose}>{t('ui.close')}</button></DialogFoot>
    </Dialog>
  );
}
